import { CheckCircle2, ChevronDown, ChevronRight, Circle, MinusCircle } from "lucide-react";
import { useState } from "react";

export type FileCadence = "monthly" | "quarterly" | "annual" | "adhoc";
export type FileCoverageStatus =
  | "received"
  | "missing"
  | "draft"
  | "outside";

export interface LegalEntityFileItem {
  id: string;
  name: string;
  period?: string;
  included?: boolean;
  onOpen?: () => void;
}

export interface FileCoverageYear {
  label: string;
  cells: FileCoverageStatus[];
  versionCount?: number;
  fileCount?: number;
}

export interface LegalEntityFileCategory {
  id: string;
  label: string;
  cadence: FileCadence;
  files: LegalEntityFileItem[];
  coverage: FileCoverageYear[];
  onOpen?: () => void;
}

export interface LegalEntityFileCoverageProps {
  totalFiles: number;
  categories: LegalEntityFileCategory[];
  emptyMessage?: string;
  contentMessage?: string;
  defaultOpen?: boolean;
}

const MONTH_INITIALS = [
  "J",
  "F",
  "M",
  "A",
  "M",
  "J",
  "J",
  "A",
  "S",
  "O",
  "N",
  "D",
];

const CADENCE_SECTIONS: {
  cadence: FileCadence;
  title: string;
}[] = [
  { cadence: "monthly", title: "Monthly files" },
  { cadence: "quarterly", title: "Quarterly files" },
  { cadence: "annual", title: "Annual files" },
  { cadence: "adhoc", title: "Other" },
];

function CoverageRows({
  cadence,
  coverage,
}: {
  cadence: FileCadence;
  coverage: FileCoverageYear[];
}) {
  if (cadence === "monthly" || cadence === "quarterly") {
    return (
      <div className="mt-0.5 space-y-px">
        {coverage.map((year) => (
          <div key={year.label} className="flex items-center gap-px">
            <span className="w-[26px] shrink-0 text-[6.5px] font-medium text-muted-foreground/70">
              {year.label}
            </span>
            {year.cells.map((status, index) => (
              <span
                key={index}
                className={`h-[9px] shrink-0 rounded-[2px] ${
                  status === "received" ? "bg-foreground" : "bg-muted"
                }`}
                style={{
                  width: cadence === "monthly" ? 9 : 29,
                }}
                title={`${year.label}${
                  cadence === "quarterly" ? ` Q${index + 1}` : ""
                }, ${status}`}
              />
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-0.5 flex items-center gap-3 px-1">
      {coverage.map((year) => {
        const status = year.cells[0] ?? "missing";
        if (cadence === "adhoc") {
          return (
            <span
              key={year.label}
              className="inline-flex shrink-0 items-baseline gap-1"
            >
              <span className="text-[6.5px] font-medium text-muted-foreground/70">
                {year.label}
              </span>
              {year.fileCount != null && (
                <>
                  <span className="text-[7.5px] font-semibold text-foreground">
                    {year.fileCount}
                  </span>
                  <span className="text-[7.5px] text-muted-foreground/50">
                    files
                  </span>
                </>
              )}
            </span>
          );
        }
        // Annual coverage reads as a mark per year, not a bar — one file
        // either arrived, arrived as a draft, or did not. Fixed-width
        // cells keep the marks aligned across the years.
        return (
          <span
            key={year.label}
            className="inline-flex w-[52px] shrink-0 items-center gap-1"
            title={`${year.label}, ${status}${
              status === "received" && (year.versionCount ?? 0) > 1
                ? `, ${year.versionCount} versions`
                : ""
            }`}
          >
            <span className="w-[19px] shrink-0 text-[6.5px] font-medium text-muted-foreground/70">
              {year.label}
            </span>
            {status === "received" ? (
              <CheckCircle2 size={11} className="shrink-0 text-foreground" />
            ) : status === "draft" ? (
              <Circle size={11} className="shrink-0 text-muted-foreground" />
            ) : (
              <MinusCircle size={11} className="shrink-0 text-muted-foreground/50" />
            )}
            {status === "received" && (year.versionCount ?? 0) > 1 && (
              <span className="text-[6px] text-muted-foreground/60">
                ×{year.versionCount}
              </span>
            )}
            {status === "draft" && (
              <span className="text-[6px] italic text-muted-foreground/60">
                draft
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

export function LegalEntityFileCoverage({
  totalFiles,
  categories,
  emptyMessage = "No analysed sources assigned",
  contentMessage,
  defaultOpen = false,
}: LegalEntityFileCoverageProps) {
  const [open, setOpen] = useState(defaultOpen);

  if (totalFiles === 0) {
    return (
      <p className="mt-1.5 text-[9px] text-muted-foreground">{emptyMessage}</p>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className="mt-1.5 flex w-full items-center gap-1 text-[9px] text-muted-foreground hover:text-foreground"
      >
        {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
        {totalFiles.toLocaleString("en-GB")} files received
      </button>
      {open && (
        <div className="mt-1 max-h-[330px] overflow-y-auto border-t border-border/60 pt-1.5">
          {contentMessage ? (
            <p className="px-1 py-0.5 text-[9.5px] text-muted-foreground">
              {contentMessage}
            </p>
          ) : CADENCE_SECTIONS.map((section) => {
            const sectionCategories = categories.filter(
              (category) => category.cadence === section.cadence,
            );
            if (sectionCategories.length === 0) return null;
            return (
              <section key={section.cadence} className="mb-1.5">
                <div className="mb-0.5 flex items-baseline gap-1 rounded bg-muted/60 px-1.5 py-0.5">
                  <span className="text-[6.5px] font-semibold uppercase tracking-wider">
                    {section.title}
                  </span>
                  <span className="text-[6.5px] text-muted-foreground/70">
                    ({sectionCategories.length})
                  </span>
                </div>
                {section.cadence === "monthly" && (
                  <div className="mb-0.5 flex items-center gap-px">
                    <span className="w-[26px]" />
                    {MONTH_INITIALS.map((month, index) => (
                      <span
                        key={`${month}-${index}`}
                        className="w-[10px] text-center text-[6.5px] text-muted-foreground/50"
                      >
                        {month}
                      </span>
                    ))}
                  </div>
                )}
                {sectionCategories.map((category) => {
                  return (
                    <div key={category.id} className="mb-1">
                      <div className="flex w-full items-center gap-1 rounded px-1 py-0.5 hover:bg-muted/50">
                        {category.onOpen ? (
                          <button
                            type="button"
                            onClick={category.onOpen}
                            className="min-w-0 flex-1 truncate text-left text-[9px] font-semibold underline decoration-dotted decoration-muted-foreground/40 underline-offset-2"
                          >
                            {category.label}
                          </button>
                        ) : (
                          <span className="min-w-0 flex-1 truncate text-left text-[9px] font-semibold">
                            {category.label}
                          </span>
                        )}
                      </div>
                      <CoverageRows
                        cadence={category.cadence}
                        coverage={category.coverage}
                      />
                    </div>
                  );
                })}
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}
